import { INTERNAL_SCHEMA_VERSION, createEntityMetadata } from "./common";
import type { ConstitutionPrinciple, ConstitutionVersion } from "./types";

const principle = (
  id: string,
  order: number,
  text: string,
  positive: string,
  negative: string,
  trigger: string,
  now: string,
): ConstitutionPrinciple => ({
  ...createEntityMetadata(
    { kind: "migration", referenceIds: [] },
    {
      id,
      now,
      status: "draft",
      exportAllowed: false,
      deletionPolicy: "retain",
    },
  ),
  schemaVersion: INTERNAL_SCHEMA_VERSION,
  order,
  text,
  positiveExamples: [positive],
  negativeExamples: [negative],
  reviewTriggers: [trigger],
  aiMutable: false,
  requiresExplicitUserApproval: true,
});

function buildDefaultPrinciples(now: string): ConstitutionPrinciple[] {
  return [
    principle(
      "00000000-0000-4000-8000-000000000001",
      1,
      "真相高于立场",
      "新证据推翻原判断后立即更新结论。",
      "为了维护此前表态而忽略新证据。",
      "结论与付款、复购、交付或现金数据冲突。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000002",
      2,
      "承认事实，拒绝歪曲",
      "承认项目未达标并说明真实差距。",
      "用选择性指标把失败包装成成功。",
      "复盘只改变叙事而没有改变行动。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000003",
      3,
      "对外可以管理叙事，对内必须绝对诚实",
      "对外克制披露，对内保留完整风险事实。",
      "为了对外口径而删除内部反证。",
      "内部记录与可验证事实不一致。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000004",
      4,
      "长期信誉高于短期利益",
      "无法按时兑现时提前说明并补救。",
      "隐瞒交付风险以促成短期成交。",
      "短期收益要求夸大、越界或失信。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000005",
      5,
      "不承担毁灭性风险",
      "在重仓前设置可承受损失和止损线。",
      "用不可承受的资金或声誉下注低概率机会。",
      "后果不可承受、不可逆或存在单点依赖。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000006",
      6,
      "决策前保留选择，决策后集中兵力",
      "决策前比较方案，确认后保护主线资源。",
      "决定后仍同时启动多个竞争项目。",
      "新机会正在抢占已确认主线资源。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000007",
      7,
      "付款、复购和转介绍高于口头赞美",
      "把付款或复购作为需求验证证据。",
      "把点赞和口头支持当作商业验证。",
      "需求判断只有赞美而没有行为证据。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000008",
      8,
      "交付先于放大",
      "先稳定交付最小闭环再扩渠道。",
      "交付尚不稳定就扩大获客和承诺。",
      "新增客户可能超过当前可靠交付能力。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000009",
      9,
      "可重复高于个人英雄主义",
      "把重复工作沉淀为 SOP、培训和权限。",
      "长期依靠创始人临场救火。",
      "同类事项第三次必须经过创始人。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000010",
      10,
      "复利高于刺激",
      "持续重复已验证有效的枯燥动作。",
      "因为无聊而频繁更换方向。",
      "新玩法出现但旧方法尚未完成足够样本。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000011",
      11,
      "盟友应当信任，恶意违规者应当忌惮",
      "保护善意合作方并对恶意违规设置明确后果。",
      "对善意者攻击，对恶意者退缩。",
      "边界被重复越过或信任关系受损。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000012",
      12,
      "允许快速承认战役失败，不放弃长期战争",
      "停止无效战役并保留长期目标。",
      "为维护面子继续投入失败战役。",
      "失败条件已满足但资源仍持续投入。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000013",
      13,
      "只宣布可衡量的胜利",
      "用付款、复购、交付或已验证指标描述进展。",
      "用势能、影响力等无验证词宣布胜利。",
      "胜利声明无法给出衡量口径和基线。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000014",
      14,
      "先由道和法确定需要，再选择术和器",
      "先明确治理问题和规则，再选择工具。",
      "因为新工具可用而改变治理重点。",
      "工具选择开始主导战略或产生额外系统建设。",
      now,
    ),
    principle(
      "00000000-0000-4000-8000-000000000015",
      15,
      "不让面子、沉没成本或身份认同阻止止损",
      "失败条件满足后按预定规则退出。",
      "因为已经投入很多而继续追加。",
      "继续投入的主要理由是过去投入或自我证明。",
      now,
    ),
  ];
}

export const DEFAULT_CONSTITUTION_PRINCIPLES: ConstitutionPrinciple[] = buildDefaultPrinciples(
  "2026-07-26T00:00:00+08:00",
);

export function createDefaultConstitution(now = new Date().toISOString()): ConstitutionVersion {
  return {
    ...createEntityMetadata(
      { kind: "migration", referenceIds: [] },
      {
        id: "00000000-0000-4000-8000-000000000100",
        now,
        status: "draft",
        exportAllowed: false,
        deletionPolicy: "retain",
      },
    ),
    version: "1.0.0",
    principles: buildDefaultPrinciples(now),
    changeSummary: "CEO OS 默认共同宪法 v1；必须由用户明确确认后激活。",
  };
}
